// Support Ticketing Types
// See docs/frontend-support-ticketing.md

export const TicketType = {
  BUG: "BUG",
  DATA_ISSUE: "DATA_ISSUE",
  QUESTION: "QUESTION",
  FEATURE_REQUEST: "FEATURE_REQUEST",
  OTHER: "OTHER",
} as const;

export type TicketType = (typeof TicketType)[keyof typeof TicketType];

export const TicketPriority = {
  LOW: "LOW",
  NORMAL: "NORMAL",
  HIGH: "HIGH",
  BLOCKING: "BLOCKING",
} as const;

export type TicketPriority =
  (typeof TicketPriority)[keyof typeof TicketPriority];

export const TicketStatus = {
  NEW: "NEW",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING_ON_CUSTOMER: "WAITING_ON_CUSTOMER",
  RESOLVED: "RESOLVED",
  CLOSED: "CLOSED",
} as const;

export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

// Open workflow statuses, per the `ticketStatus=OPEN` filter shortcut
export const OPEN_TICKET_STATUSES: TicketStatus[] = [
  TicketStatus.NEW,
  TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_ON_CUSTOMER,
];

export interface SupportTicketListItem {
  id: number;
  ticketNumber: string;
  subject: string;
  type: TicketType;
  priority: TicketPriority;
  ticketStatus: TicketStatus;
  tenantId: number;
  companyName: string;
  createdByUserId: number;
  createdByEmail: string;
  assigneeUserId: number | null;
  assigneeEmail: string | null;
  createdAt: string;
  lastActivityAt: string;
  replyCount: number;
  unread: boolean;
}

export interface TicketAttachment {
  id: number;
  messageId: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface TicketMessage {
  id: number;
  authorUserId: number;
  authorEmail: string;
  body: string;
  internal: boolean;
  createdAt: string;
  attachments: TicketAttachment[];
}

export interface TicketStatusHistoryEntry {
  id: number;
  fromTicketStatus: TicketStatus;
  toTicketStatus: TicketStatus;
  explanation: string;
  changedByUserId: number;
  changedByEmail: string;
  changedAt: string;
}

export interface SupportTicketDetail extends SupportTicketListItem {
  pageUrl?: string;
  browser?: string;
  os?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  screenWidth?: number;
  screenHeight?: number;
  appVersion?: string;
  requestId?: string;
  messages: TicketMessage[];
  attachments: TicketAttachment[];
  statusHistory: TicketStatusHistoryEntry[];
  // Staff-only; absent/null for end users
  ipAddress?: string | null;
  geoCity?: string | null;
  geoRegion?: string | null;
  geoCountry?: string | null;
  geoIsp?: string | null;
  userAgent?: string | null;
}

export interface SupportTicketPage {
  data: SupportTicketListItem[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface SupportTicketFilters {
  page?: number;
  pageSize?: number;
  ticketStatus?: TicketStatus | "OPEN";
  type?: TicketType;
  priority?: TicketPriority;
  assigneeUserId?: number;
  unassignedOnly?: boolean;
  companyTenantId?: number;
  unreadOnly?: boolean;
  search?: string;
}

export interface CreateTicketRequest {
  type: TicketType;
  priority: TicketPriority;
  subject: string;
  body: string;
  pageUrl?: string;
  browser?: string;
  os?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  screenWidth?: number;
  screenHeight?: number;
  appVersion?: string;
  requestId?: string;
}

export interface ReplyToTicketRequest {
  body: string;
  internal?: boolean;
}

export interface UpdateTicketStatusRequest {
  ticketStatus: TicketStatus;
  explanation: string;
}

export interface AssignTicketRequest {
  assigneeUserId: number | null;
}

export interface TicketAssignee {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
}

export interface AttachmentDownloadUrl {
  downloadUrl: string;
}

export interface InitTicketAttachmentRequest {
  fileName: string;
  contentType: string;
  sizeBytes?: number;
  messageId?: number;
}

export interface InitTicketAttachmentResponse {
  attachmentId: number;
  uploadUrl: string;
  s3Bucket: string;
  s3Key: string;
  fileName: string;
  contentType: string;
  expiresIn: number;
}

export interface SupportAnalyticsBreakdownRow {
  tenantId: number;
  companyName: string;
  created: number;
  open: number;
  resolved: number;
}

export interface SupportAnalyticsSummary {
  fromDate: string;
  toDate: string;
  created: number;
  resolved: number;
  openBacklog: number;
  medianFirstResponseHours: number | null;
  medianResolutionHours: number | null;
  reopenRatePercent: number;
  breakdown: SupportAnalyticsBreakdownRow[];
}

export interface SupportAnalyticsFilters {
  fromDate: string;
  toDate: string;
}

export type SupportAnalyticsTrendBucket = "day" | "week" | "month";

export interface SupportAnalyticsTrendPoint {
  periodStart: string;
  count: number;
}

export interface SupportAnalyticsTrend {
  bucket: SupportAnalyticsTrendBucket;
  created: SupportAnalyticsTrendPoint[];
  resolved: SupportAnalyticsTrendPoint[];
}

export interface SupportAnalyticsTrendFilters extends SupportAnalyticsFilters {
  bucket?: SupportAnalyticsTrendBucket;
}

export interface SoftDeleteTicketResult {
  deleted: boolean;
  id: number;
}
